import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface SupportSession {
  session_id: string;
  org_id: string;
  org_name: string;
  expires_at: string;
}

/** The platform admin's active support session, if any. Null for everyone else. */
export function useSupportSession() {
  const { user } = useAuth();
  const q = useQuery({
    queryKey: ["support-session", user?.id],
    enabled: !!user,
    staleTime: 30_000,
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("admin_my_support" as any);
      if (error) return null;
      return (data as SupportSession | null) ?? null;
    },
  });
  return { session: q.data ?? null, loading: q.isLoading, refetch: q.refetch };
}

/** Workspace the signed-in person is working in: the support workspace first, else their first managed one. */
export async function resolveCurrentOrg(userId: string): Promise<{ org_id: string; org_name: string; my_role: "owner" | "admin" | "member" } | null> {
  const { data: s } = await supabase.rpc("admin_my_support" as any);
  const support = s as SupportSession | null;
  if (support?.org_id) return { org_id: support.org_id, org_name: support.org_name, my_role: "admin" };
  const { data, error } = await supabase
    .from("managers")
    .select("org_id, role, orgs!inner(id, name)")
    .eq("user_id", userId)
    .order("created_at", { ascending: true })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return { org_id: data.org_id as string, org_name: (data as any).orgs?.name as string, my_role: data.role as any };
}

export interface ActingAccount {
  /** Who new records belong to: the supported workspace's owner in support mode, else me. */
  user_id: string;
  /** The supported workspace in support mode; null otherwise (keeps normal behaviour unchanged). */
  org_id: string | null;
  support: boolean;
}

/** The one place the app decides whose account and workspace new records go into. */
export async function getActingAccount(fallbackUserId: string): Promise<ActingAccount> {
  const { data, error } = await supabase.rpc("my_acting_account" as any);
  const a = data as ActingAccount | null;
  if (error || !a?.user_id) return { user_id: fallbackUserId, org_id: null, support: false };
  return a;
}

/** Hook form of getActingAccount, for queries that read "my" settings. */
export function useActingAccount() {
  const { user } = useAuth();
  const { session } = useSupportSession();
  const q = useQuery({
    queryKey: ["acting-account", user?.id, session?.session_id ?? null],
    enabled: !!user,
    staleTime: 30_000,
    queryFn: () => getActingAccount(user!.id),
  });
  return q.data ?? null;
}
